import i18n from "i18next";
import "../i18n";

/**
 * User-facing copy for verification confirm-code failures (GROWTH-6685).
 *
 * 429 / 409 carry an actionable API message. 404 stays the caller's existing
 * "wrong code" string so we do not surface the backend's generic 404 sentence.
 *
 * @param {import("axios").AxiosError|Error|undefined} error
 * @param {{ wrongCode: string, fallback: string }} copy
 * @returns {string}
 */
export function verificationConfirmErrorCopy(error, { wrongCode, fallback }) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const apiMessage = data?.message || data?.error || data?.data?.error;

  if (status === 429 || status === 409) {
    return apiMessage || fallback;
  }

  if (status === 404) {
    return wrongCode;
  }

  return fallback;
}

const listingConfirmApiMessage = error => {
  const data = error?.response?.data;
  return data?.data?.error || data?.message || data?.error || "";
};

/** Shared listing confirm-code copy (email and phone thunks). */
// Getters, not strings: the text is read from the language bundle each time the copy is used, never frozen at import.
export const LISTING_CONFIRM_CODE_COPY = {
  get wrongCode() {
    return i18n.t("dashboard_verify_code_incorrect");
  },
  get fallback() {
    return i18n.t("dashboard_verify_code_incorrect");
  }
};

/**
 * Listing Add Recipient confirm-code fail payload (GROWTH-6533 + GROWTH-6537).
 * 403 surfaces the access-denied API string. 404 stays GROWTH-6685 wrong-code
 * copy and sets canResend. Missing HTTP response is a save/network failure,
 * not an invalid code.
 *
 * @param {import("axios").AxiosError|Error|undefined} error
 * @param {{ wrongCode: string, fallback: string }} copy
 * @returns {{ message?: string, saveError?: string, canResend?: boolean }}
 */
export function listingConfirmCodeFailPayload(error, copy) {
  if (!error?.response) {
    return { saveError: i18n.t("dashboard_common_error_try_again") };
  }

  const canResend = error.response.status === 404;

  if (error.response.status === 403) {
    return {
      message: listingConfirmApiMessage(error) || i18n.t("dashboard_listing_confirm_error_access"),
      canResend
    };
  }

  return { message: verificationConfirmErrorCopy(error, copy), canResend };
}
